import React, { useCallback, useContext, useEffect, useState } from "react";
import Moment from "moment";
import { useHistory, useLocation } from "react-router-dom";

import { CustomersContext } from "../../context/customersContext";
import CustomersTable from "../tables/CustomersTable";
import CustomerModal from "../modals/CustomerModal";
import { DEFAULT_CUSTOMERS_PAGE_SIZE, resolveApiLimit } from "../../constants/config";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { Card, EmptyState, PageHeader, SearchBar, Spinner } from "../ui";

const FILTER_TO_CONTRACTOR = {
    All: undefined,
    Contractors: "Yes",
    Customers: "No",
};

const formatPhone = (mobile) => {
    if (!mobile) return "";
    return `(${mobile.slice(0, 3)}) ${mobile.slice(3, 6)}-${mobile.slice(6)}`;
};

const Customers = () => {
    const { action, customersPage, customersPagination, customersPageLoading } =
        useContext(CustomersContext);

    const location = useLocation();
    const history = useHistory();

    const [filter, setFilter] = useState(
        () => new URLSearchParams(location.search).get("filter") || "All",
    );
    const [balanceFilter, setBalanceFilter] = useState(
        () => new URLSearchParams(location.search).get("balance") || "All",
    );
    const [search, setSearch] = useState(
        () => new URLSearchParams(location.search).get("q") || "",
    );
    const [page, setPage] = useState(() => {
        const p = new URLSearchParams(location.search).get("page");
        return p !== null ? Math.max(0, parseInt(p, 10)) : 0;
    });
    const [rowsPerPage, setRowsPerPage] = useState(() => {
        const s = new URLSearchParams(location.search).get("size");
        return s !== null ? parseInt(s, 10) : DEFAULT_CUSTOMERS_PAGE_SIZE;
    });
    const [sortBy, setSortBy] = useState(
        () => new URLSearchParams(location.search).get("sortBy") || "firstName",
    );
    const [sortOrder, setSortOrder] = useState(
        () => new URLSearchParams(location.search).get("sortOrder") || "asc",
    );

    const debouncedSearch = useDebouncedValue(search);

    useEffect(() => {
        const params = new URLSearchParams();
        if (page !== 0) params.set("page", page);
        if (rowsPerPage !== DEFAULT_CUSTOMERS_PAGE_SIZE) params.set("size", rowsPerPage);
        if (sortBy !== "firstName") params.set("sortBy", sortBy);
        if (sortOrder !== "asc") params.set("sortOrder", sortOrder);
        if (filter !== "All") params.set("filter", filter);
        if (balanceFilter !== "All") params.set("balance", balanceFilter);
        if (search) params.set("q", search);
        history.replace({ search: params.toString() });
    }, [page, rowsPerPage, sortBy, sortOrder, filter, balanceFilter, search, history]);

    useEffect(() => {
        action.getCustomersPage({
            page: page + 1,
            limit: resolveApiLimit(rowsPerPage),
            search: debouncedSearch || undefined,
            contractor: FILTER_TO_CONTRACTOR[filter],
            balance:
                balanceFilter === "Overdue"
                    ? "overdue"
                    : balanceFilter === "Cleared"
                      ? "cleared"
                      : undefined,
            withStats: true,
            sortBy,
            sortOrder,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, debouncedSearch, filter, balanceFilter, sortBy, sortOrder]);

    const handleSortChange = useCallback((field, direction) => {
        setSortBy(field);
        setSortOrder(direction);
        setPage(0);
    }, []);

    const [open, setOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState();

    const handleClickOpen = async (customerId) => {
        setOpen(true);
        const customer = await action.getCustomer(customerId);
        setSelectedCustomer(customer);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedCustomer();
    };

    const refreshCustomersList = () => {
        action.getCustomersList();
        action.getCustomersPage({
            page: page + 1,
            limit: resolveApiLimit(rowsPerPage),
            search: debouncedSearch || undefined,
            contractor: FILTER_TO_CONTRACTOR[filter],
            balance:
                balanceFilter === "Overdue"
                    ? "overdue"
                    : balanceFilter === "Cleared"
                      ? "cleared"
                      : undefined,
            withStats: true,
        });
    };

    const list = customersPage || [];
    const totalCount = (customersPagination && customersPagination.totalCustomers) || 0;
    const initialLoad = !customersPage;

    return (
        <div className="space-y-6">
            <PageHeader
                title="Customers"
                description="Search, filter, and manage your customers and contractors."
            />

            <div className="md:hidden">
                <SearchBar
                    id="customer-search"
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(0);
                    }}
                    placeholder="Search name, phone, city, or company"
                />
            </div>

            {/* Mobile card list */}
            <div className="md:hidden">
                {initialLoad ? (
                    <div className="flex justify-center py-12">
                        <Spinner size="lg" />
                    </div>
                ) : list.length === 0 ? (
                    <EmptyState
                        icon={<i className="fas fa-user" />}
                        title="No customers found"
                        description="Try adjusting your search or filters."
                    />
                ) : (
                    <ul className="space-y-3">
                        {list.map((row) => {
                            const balance = Number(row.balance || 0);
                            return (
                                <li key={row._id}>
                                    <button
                                        type="button"
                                        onClick={() => handleClickOpen(row._id)}
                                        className="block w-full text-left"
                                    >
                                        <Card className="hover:border-brand-200 hover:shadow-elevated">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                    <p className="font-semibold text-ink-900">
                                                        {row.firstName} {row.lastName}
                                                    </p>
                                                    <p className="text-xs text-ink-500">
                                                        {formatPhone(row.mobile)}
                                                    </p>
                                                    {row.city && (
                                                        <p className="text-xs text-ink-500">
                                                            {row.city}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="text-right">
                                                    <p
                                                        className={`text-sm font-semibold ${
                                                            balance > 0
                                                                ? "text-danger-600"
                                                                : "text-ink-700"
                                                        }`}
                                                    >
                                                        {balance === 0
                                                            ? "—"
                                                            : `$${balance.toFixed(2)}`}
                                                    </p>
                                                    {row.latestActivity && (
                                                        <p className="mt-0.5 text-xs text-ink-500">
                                                            {Moment(row.latestActivity).format(
                                                                "MMM D",
                                                            )}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </Card>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}

                {customersPagination && customersPagination.totalPages > 1 && (
                    <div className="mt-4 flex items-center justify-between text-sm">
                        <button
                            type="button"
                            className="text-brand-600 disabled:text-ink-300"
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={page === 0}
                        >
                            Previous
                        </button>
                        <span className="text-ink-500">
                            Page {page + 1} of {customersPagination.totalPages}
                        </span>
                        <button
                            type="button"
                            className="text-brand-600 disabled:text-ink-300"
                            onClick={() => setPage((p) => p + 1)}
                            disabled={page + 1 >= customersPagination.totalPages}
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block">
                {initialLoad ? (
                    <div className="flex justify-center py-12">
                        <Spinner size="lg" />
                    </div>
                ) : (
                    <CustomersTable
                        customers={list}
                        handleClickOpen={handleClickOpen}
                        refreshCustomersList={refreshCustomersList}
                        action={action}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        totalCount={totalCount}
                        onPageChange={setPage}
                        onRowsPerPageChange={(rows) => {
                            setRowsPerPage(rows);
                            setPage(0);
                        }}
                        loading={customersPageLoading}
                        search={search}
                        onSearchChange={(value) => {
                            setSearch(value);
                            setPage(0);
                        }}
                        onSortChange={handleSortChange}
                        defaultSort={{ id: sortBy, direction: sortOrder }}
                        filters={[
                            {
                                id: "contractor",
                                label: "Filter by type",
                                value: filter,
                                onChange: (value) => {
                                    setFilter(value);
                                    setPage(0);
                                },
                                options: [
                                    { value: "All", label: "All", tone: "brand" },
                                    { value: "Customers", label: "Customers", tone: "blue" },
                                    {
                                        value: "Contractors",
                                        label: "Contractors",
                                        tone: "darkBlue",
                                    },
                                ],
                            },
                            {
                                id: "balance",
                                label: "Filter by balance",
                                value: balanceFilter,
                                onChange: (value) => {
                                    setBalanceFilter(value);
                                    setPage(0);
                                },
                                options: [
                                    { value: "All", label: "All", tone: "brand" },
                                    { value: "Overdue", label: "Overdue", tone: "red" },
                                    { value: "Cleared", label: "Cleared", tone: "green" },
                                ],
                            },
                        ]}
                        emptyMessage={
                            filter === "All" && balanceFilter === "All"
                                ? "No customers to display."
                                : "No customers match the selected filters."
                        }
                    />
                )}
            </div>

            <CustomerModal
                selectedCustomer={selectedCustomer}
                open={open}
                setOpen={setOpen}
                handleClose={handleClose}
                handleClickOpen={handleClickOpen}
            />
        </div>
    );
};

export default Customers;
